/**
 * Iter 122b · Página pública para subir arte tras pago
 * Ruta: /tienda/pegatinas/mi-arte?order=XXX&token=YYY
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, CheckCircle2, FileText, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API, withCredentials: false });

const ESTADO_TEXTOS = {
  en_revision_diseno: {
    title: "Sube tu diseño para producir",
    subtitle: "Nuestra diseñadora revisará tu archivo antes de imprimir.",
    color: "text-fuchsia-700",
  },
  revision_cambios: {
    title: "Necesitamos ajustes en tu diseño",
    subtitle: "Sube una versión corregida y volveremos a revisarla.",
    color: "text-amber-700",
  },
  revision_aprobada: {
    title: "¡Diseño aprobado!",
    subtitle: "Tu pedido está en cola de producción.",
    color: "text-emerald-700",
  },
  enviado: {
    title: "¡Pedido enviado!",
    subtitle: "Ya está en camino.",
    color: "text-blue-700",
  },
  cancelado_reembolsado: {
    title: "Pedido cancelado",
    subtitle: "Se ha emitido un reembolso a tu tarjeta.",
    color: "text-red-700",
  },
};

export default function TiendaSubirArte() {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const order = params.get("order");
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await publicApi.get(`/public/tienda/pedidos/${order}/upload-info`, { params: { token } });
      setInfo(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo cargar el pedido");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!order || !token) { setErr("Enlace inválido"); setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Máximo 25MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await publicApi.post(
        `/public/tienda/pedidos/${order}/subir-arte`,
        fd,
        { params: { token }, headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Archivo subido");
      await load();
      if (inputRef.current) inputRef.current.value = "";
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error subiendo archivo");
    }
    setUploading(false);
  };

  const estadoInfo = ESTADO_TEXTOS[info?.estado] || {};
  const puedeSubir = info?.estado === "en_revision_diseno" || info?.estado === "revision_cambios";

  return (
    <div className="tienda-scope min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <a href="/tienda/pegatinas" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center mb-4">
          ← Volver a la tienda
        </a>
        {loading && (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
          </div>
        )}
        {err && !loading && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <h1 className="text-lg font-bold text-red-800">Enlace inválido</h1>
              <p className="text-sm text-red-700 mt-1">{err}</p>
            </CardContent>
          </Card>
        )}
        {info && !loading && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="text-xs text-slate-500">Pedido {order} · {info.email}</div>
              <h1 className={`text-2xl font-bold mt-1 ${estadoInfo.color}`}>{estadoInfo.title}</h1>
              <p className="text-slate-600 text-sm mt-1">{estadoInfo.subtitle}</p>

              <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-4 text-sm">
                <div><span className="text-slate-500">Material:</span> {info.material_nombre}</div>
                <div><span className="text-slate-500">Cantidad:</span> {info.cantidad_total} uds</div>
              </div>

              {info.estado === "revision_cambios" && info.notas_diseñadora && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4 text-sm">
                  <div className="font-semibold text-amber-800 mb-1">Notas de la diseñadora:</div>
                  <div className="text-amber-900 whitespace-pre-wrap">{info.notas_diseñadora}</div>
                </div>
              )}

              {/* Archivos subidos */}
              {(info.arte_files || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-600 mb-1">Archivos subidos</div>
                  <div className="space-y-1">
                    {info.arte_files.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded p-2 hover:border-fuchsia-400"
                        data-testid={`arte-file-${i}`}>
                        <FileText className="w-4 h-4 text-fuchsia-600" />
                        <span className="flex-1 truncate">{a.filename}</span>
                        <span className="text-[10px] text-slate-400">
                          {new Date(a.uploaded_at).toLocaleDateString("es-ES")}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {puedeSubir && (
                <div className="mt-6 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <div className="text-sm text-slate-600 mb-3">
                    PDF, SVG, AI, PNG o JPG · máx 25MB
                  </div>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.svg,.ai,.eps,.png,.jpg,.jpeg,application/pdf,image/*"
                    onChange={onFile}
                    className="hidden"
                    data-testid="upload-input"
                  />
                  <Button
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="upload-btn">
                    {uploading ? "Subiendo…" : ((info.arte_files || []).length > 0 ? "Subir nueva versión" : "Elegir archivo")}
                  </Button>
                </div>
              )}

              {info.estado === "revision_aprobada" && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mt-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-1" />
                  <div className="text-sm text-emerald-800">Tu pedido está en producción. Te avisaremos cuando salga.</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
